import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Heart, Shield, Star, ArrowRight, Users, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

export default function Landing() {
  const { user, login, signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('welcome');
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'teen', age: '', school: '', parent_email: '', organization_name: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup({
        ...form,
        age: form.age ? parseInt(form.age) : null,
      });
      toast.success('Welcome to TeenHands!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'welcome') {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col" data-testid="landing-page">
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 max-w-lg mx-auto">
          <div className="animate-fade-in-up">
            <div className="w-20 h-20 bg-[#2EC4B6] rounded-3xl flex items-center justify-center mb-6 mx-auto shadow-lg animate-float">
              <Heart size={40} className="text-white" strokeWidth={3} />
            </div>
            <h1 className="text-5xl font-bold text-center text-[#011627] mb-3 tracking-tight">TeenHands</h1>
            <p className="text-center text-[#5C6B7F] text-lg font-semibold mb-10">
              Find safe gigs & volunteer opportunities in your community
            </p>
          </div>

          <div className="w-full space-y-6 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
            <div className="grid grid-cols-3 gap-3 mb-8">
              {[
                { icon: Shield, label: 'Safe & Verified', color: '#2EC4B6' },
                { icon: Star, label: 'Earn Badges', color: '#FF9F1C' },
                { icon: Users, label: 'Community', color: '#6C63FF' },
              ].map(({ icon: Icon, label, color }) => (
                <div key={label} className="bg-white rounded-2xl p-4 flex flex-col items-center gap-2 border-2 border-gray-100">
                  <Icon size={28} style={{ color }} strokeWidth={2.5} />
                  <span className="text-xs font-bold text-[#5C6B7F] text-center">{label}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={() => setMode('login')}
              className="w-full rounded-full py-6 text-lg font-bold bg-[#2EC4B6] hover:bg-[#25A296] text-white btn-bounce"
              data-testid="get-started-btn"
            >
              Get Started <ArrowRight size={20} className="ml-2" />
            </Button>

            <Button
              onClick={loginWithGoogle}
              variant="outline"
              className="w-full rounded-full py-6 text-lg font-bold border-2 border-gray-200 hover:border-[#2EC4B6] btn-bounce"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <p className="text-center text-sm text-[#9AA5B1] font-semibold">
              Safe for teens 13-18 &middot; Parent-approved
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col" data-testid="auth-page">
      <SEO
        title="Sign Up & Log In"
        description="Join TeenHands to find local volunteer opportunities and paid side gigs for teens ages 13–18. Sign up or log in to get started."
        path="/"
      />
      <div className="flex-1 flex flex-col px-6 py-8 max-w-lg mx-auto w-full">
        <button onClick={() => setMode('welcome')} className="text-[#5C6B7F] font-bold mb-6 self-start" data-testid="back-btn">
          &larr; Back
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#2EC4B6] rounded-2xl flex items-center justify-center">
            <Sparkles size={24} className="text-white" strokeWidth={3} />
          </div>
          <h2 className="text-3xl font-bold text-[#011627]">{mode === 'login' ? 'Welcome Back!' : 'Join TeenHands'}</h2>
        </div>

        <div className="flex bg-gray-100 rounded-full p-1 mb-6">
          <button
            onClick={() => setMode('login')}
            className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all ${mode === 'login' ? 'bg-white text-[#011627] shadow-sm' : 'text-[#5C6B7F]'}`}
            data-testid="login-tab"
          >
            Log In
          </button>
          <button
            onClick={() => setMode('signup')}
            className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all ${mode === 'signup' ? 'bg-white text-[#011627] shadow-sm' : 'text-[#5C6B7F]'}`}
            data-testid="signup-tab"
          >
            Sign Up
          </button>
        </div>

        {mode === 'login' ? (
          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <Input
              type="email" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
              data-testid="login-email" required
            />
            <Input
              type="password" placeholder="Password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
              data-testid="login-password" required
            />
            <Button
              type="submit" disabled={loading}
              className="w-full rounded-full py-6 text-lg font-bold bg-[#2EC4B6] hover:bg-[#25A296] text-white btn-bounce"
              data-testid="login-submit"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleSignup} className="space-y-4" data-testid="signup-form">
            <Input
              placeholder="Full Name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
              data-testid="signup-name" required
            />
            <Input
              type="email" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
              data-testid="signup-email" required
            />
            <Input
              type="password" placeholder="Password (6+ characters)" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
              data-testid="signup-password" required minLength={6}
            />

            <div className="flex gap-2">
              {['teen', 'organization'].map((r) => (
                <button
                  key={r} type="button"
                  onClick={() => setForm({ ...form, role: r })}
                  className={`flex-1 py-3 rounded-2xl font-bold text-sm border-2 transition-all ${
                    form.role === r ? 'border-[#2EC4B6] bg-[#2EC4B6]/10 text-[#2EC4B6]' : 'border-gray-100 text-[#5C6B7F]'
                  }`}
                  data-testid={`role-${r}`}
                >
                  {r === 'teen' ? 'I\'m a Teen' : 'Organization'}
                </button>
              ))}
            </div>

            {form.role === 'teen' && (
              <>
                <Input
                  type="number" placeholder="Age (13-18)" value={form.age} min={13} max={18}
                  onChange={(e) => setForm({ ...form, age: e.target.value })}
                  className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
                  data-testid="signup-age"
                />
                <Input
                  placeholder="School (optional)" value={form.school}
                  onChange={(e) => setForm({ ...form, school: e.target.value })}
                  className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
                  data-testid="signup-school"
                />
              </>
            )}

            {form.role === 'organization' && (
              <Input
                placeholder="Organization Name" value={form.organization_name}
                onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
                data-testid="signup-org-name"
              />
            )}

            <Button
              type="submit" disabled={loading}
              className="w-full rounded-full py-6 text-lg font-bold bg-[#FF9F1C] hover:bg-[#E58E19] text-white btn-bounce"
              data-testid="signup-submit"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
        )}

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t-2 border-gray-100" /></div>
          <div className="relative flex justify-center text-sm"><span className="bg-[#FDFBF7] px-3 text-[#9AA5B1] font-bold">or</span></div>
        </div>

        <Button
          onClick={loginWithGoogle}
          variant="outline"
          className="w-full rounded-full py-5 font-bold border-2 border-gray-200 hover:border-[#2EC4B6] btn-bounce"
          data-testid="google-auth-btn"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>
      </div>
    </div>
  );
}
